import React, {useState, useEffect } from 'react';
import {useNavigate } from 'react-router-dom';
import {getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import {doc, setDoc } from 'firebase/firestore'; // For initial user profile setup
import {db } from '../../firebase';

import {
  SignupPageContainer,
  SignupForm,
  Title,
  FormGroup,
  Label,
  Input,
  Button,
  StyledLink,
  MessageContainer,
} from './styles/Signup.styles'; // Keep existing styles, remove payment specific ones if they cause issues

const Signup = () => {
  const auth = getAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'error' | 'success'; text: string } | null>(null);

  const handleEmailSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    if (password !== confirmPassword) {
      setMessage({type: 'error', text: 'Passwords do not match!' });
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await updateProfile(user, {displayName: name });

      // Save initial user data to Firestore
      // You can decide what initial data to save here.
      // We won't save stallNumber or subscription here, that's for BuyStall.tsx
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        name: name,
        email: user.email,
        createdAt: new Date(),
        // No stallNumber, businessName, or subscription yet
        profileComplete: true, // Mark basic profile complete
      });

      setMessage({type: 'success', text: 'Account created successfully! Redirecting to buy a stall...' });
      // Redirect to the new Buy Stall page
      navigate('/buy-stall');

    } catch (error: any) {
      console.error("Email signup error:", error);
      let errorMessage = 'Failed to sign up. Please try again.';

      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'Email already in use. Please login or use a different email.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password should be at least 6 characters.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Check your connection.';
            break;
          default:
            errorMessage = `Error: ${error.message}`;
            break;
        }
      }
      setMessage({type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignupPageContainer>
      <SignupForm onSubmit={handleEmailSignup}>
        <Title>Create Your Account</Title>

        {message && <MessageContainer type={message.type}>{message.text}</MessageContainer>}

        <FormGroup>
          <Label htmlFor="name">Your Full Name</Label>
          <Input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., John Doe"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="your@example.com"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Min 6 characters"
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            placeholder="Re-enter password"
          />
        </FormGroup>

        <Button type="submit" disabled={loading}>
          {loading ? 'Registering...' : 'Register'}
        </Button>

        <StyledLink to="/login">Already have an account? Login</StyledLink>
      </SignupForm>
    </SignupPageContainer>
  );
};

export default Signup;